import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { Document, DocumentCategory, documentService, EntityType } from '@/services/documentService';
import {
  AlertCircle,
  CheckCircle,
  Download,
  File,
  FileText,
  Image,
  RefreshCw,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface AttachmentFile {
  id: string;
  file?: File;
  category: string;
  uploadProgress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  errorMessage?: string;
  url?: string;
  documentId?: string;
  awbNumber?: string;
  // For existing documents
  isExisting?: boolean;
  document?: Document;
}

interface AttachmentUploaderProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedTypes?: string[];
  requiredCategories?: string[];
  disabled?: boolean;
  awbNumber?: string;
  // New prop to load existing documents
  loadExistingDocuments?: boolean;
}

const attachmentCategories = [
  'Shipment Invoice Excel',
  'Address Labels', 
  'Authorization Letter',
  'KYC Documents',
  'Commercial Invoice',
  'Packing List',
  'Certificate of Origin',
  'Insurance Certificate',
  'Other Documents'
];

const requiredCategories = [
  'Shipment Invoice Excel',
  'Address Labels',
  'Authorization Letter'
];

// Map frontend categories to backend categories
const categoryMapping: Record<string, DocumentCategory> = {
  'Shipment Invoice Excel': DocumentCategory.INVOICE,
  'Address Labels': DocumentCategory.SHIPPING_LABEL, 
  'Authorization Letter': DocumentCategory.AUTHORIZATION,
  'KYC Documents': DocumentCategory.KYC,
  'Commercial Invoice': DocumentCategory.INVOICE,
  'Packing List': DocumentCategory.OTHER,
  'Certificate of Origin': DocumentCategory.CERTIFICATE,
  'Insurance Certificate': DocumentCategory.CERTIFICATE,
  'Other Documents': DocumentCategory.OTHER
};

// Reverse mapping for display
const reverseCategoryMapping: Record<DocumentCategory, string> = {
  [DocumentCategory.KYC]: 'KYC Documents',
  [DocumentCategory.INVOICE]: 'Shipment Invoice Excel',
  [DocumentCategory.SHIPPING_LABEL]: 'Address Labels',
  [DocumentCategory.AUTHORIZATION]: 'Authorization Letter',
  [DocumentCategory.PROOF_OF_DELIVERY]: 'Proof of Delivery',
  [DocumentCategory.CUSTOMS]: 'Customs Documents',
  [DocumentCategory.MANIFEST]: 'Manifest Documents',
  [DocumentCategory.BILLING]: 'Billing Documents',
  [DocumentCategory.CONTRACT]: 'Contract Documents',
  [DocumentCategory.CERTIFICATE]: 'Certificate Documents',
  [DocumentCategory.REPORT]: 'Report Documents',
  [DocumentCategory.OTHER]: 'Other Documents'
};

const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  onFilesChange,
  maxFiles = 10,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx'],
  requiredCategories: propRequiredCategories = requiredCategories,
  disabled = false,
  awbNumber,
  loadExistingDocuments = false
}) => {
  const { toast } = useToast();
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [attachmentQuery, setAttachmentQuery] = useState('');
  const [attachmentStatusFilter, setAttachmentStatusFilter] = useState<
    'all' | 'pending' | 'uploading' | 'completed' | 'error' | 'existing'
  >('all');
  const lastCompletedSignatureRef = useRef<string | null>(null);

  // Load existing documents when component mounts or awbNumber changes (only in edit mode)
  useEffect(() => {
    if (loadExistingDocuments && awbNumber && awbNumber !== 'temp-awb') {
      loadExistingDocumentsFromAPI();
    }
  }, [loadExistingDocuments, awbNumber]);

  const loadExistingDocumentsFromAPI = async () => {
    if (!awbNumber || awbNumber === 'temp-awb') return;
    
    setIsLoadingExisting(true);
    try {
      const documents = await documentService.getDocumentsByEntity(EntityType.AWB, awbNumber);
      
      const existingAttachments: AttachmentFile[] = documents.map(doc => ({
        id: doc._id,
        category: reverseCategoryMapping[doc.category] || 'Other Documents',
        uploadProgress: 100,
        status: 'completed' as const,
        documentId: doc._id,
        url: doc.url,
        awbNumber,
        isExisting: true,
        document: doc
      }));

      setAttachments(prev => {
        // Remove any existing documents and add the fresh ones
        const newFiles = prev.filter(att => !att.isExisting);
        return [...newFiles, ...existingAttachments];
      });

      toast({
        title: 'Documents Loaded',
        description: `Loaded ${documents.length} existing documents.`,
      });

    } catch (error) {
      console.error('Error loading existing documents:', error);
      toast({
        title: 'Warning',
        description: 'Could not load existing documents. You can still upload new ones.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingExisting(false);
    }
  };

  const getFileIcon = (attachment: AttachmentFile) => {
    if (attachment.isExisting && attachment.document) {
      if (attachment.document.mimeType.startsWith('image/')) {
        return <Image className="h-4 w-4" />;
      } else if (attachment.document.mimeType === 'application/pdf') {
        return <FileText className="h-4 w-4" />;
      } else {
        return <File className="h-4 w-4" />;
      }
    } else if (attachment.file) {
      if (attachment.file.type.startsWith('image/')) {
        return <Image className="h-4 w-4" />;
      } else if (attachment.file.type === 'application/pdf') {
        return <FileText className="h-4 w-4" />;
      } else {
        return <File className="h-4 w-4" />;
      }
    }
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File size exceeds ${formatFileSize(maxFileSize)} limit`;
    }

    // Check file type
    const isValidType = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      } else if (type.includes('/*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      } else {
        return file.type === type;
      }
    });

    if (!isValidType) {
      return `File type not supported. Accepted types: ${acceptedTypes.join(', ')}`;
    }

    return null;
  };

  // Real upload function using the new document service
  const performRealUpload = async (attachment: AttachmentFile) => {
    if (!attachment.category) {
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, status: 'error', errorMessage: 'Please select a category before uploading' }
          : att
      ));
      return;
    }

    if (!attachment.file) {
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, status: 'error', errorMessage: 'No file to upload' }
          : att
      ));
      return;
    }

    try {
      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { ...att, status: 'uploading', uploadProgress: 0 }
          : att
      ));

      const uploadRequest = {
        file: attachment.file,
        category: categoryMapping[attachment.category] || DocumentCategory.OTHER,
        entityType: EntityType.AWB,
        entityId: awbNumber || 'temp-awb',
        description: `${attachment.category} for AWB ${awbNumber || 'draft'}`,
        accessLevel: 'INTERNAL' as any
      };

      const document = await documentService.uploadDocument(uploadRequest);

      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { 
              ...att, 
              status: 'completed', 
              uploadProgress: 100,
              documentId: document._id,
              url: document.url,
              document: document
            }
          : att
      ));

      toast({
        title: 'Upload Successful',
        description: `${attachment.file.name} has been uploaded successfully.`,
      });

    } catch (error: any) {
      console.error('Upload error:', error);
      
      const errorMessage = error.response?.data?.error?.message || 
                          error.message || 
                          'Upload failed. Please try again.';

      setAttachments(prev => prev.map(att => 
        att.id === attachment.id 
          ? { 
              ...att, 
              status: 'error', 
              uploadProgress: 0,
              errorMessage 
            }
          : att
      ));

      toast({
        title: 'Upload Failed',
        description: `${attachment.file?.name}: ${errorMessage}`,
        variant: 'destructive',
      });
    }
  };

  const completedFiles = useMemo(
    () =>
      attachments
        .filter(att => att.status === 'completed' && att.file)
        .map(att => att.file!),
    [attachments]
  );

  const completedSignature = useMemo(
    () =>
      completedFiles
        .map(file => `${file.name}-${file.size}-${file.lastModified}`)
        .join('|'),
    [completedFiles]
  );

  useEffect(() => {
    if (lastCompletedSignatureRef.current === completedSignature) return;
    lastCompletedSignatureRef.current = completedSignature;
    onFilesChange(completedFiles);
  }, [completedSignature, completedFiles, onFilesChange]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || disabled) return;

    const fileArray = Array.from(files);
    
    // Check max files limit
    if (attachments.length + fileArray.length > maxFiles) {
      toast({
        title: 'Too Many Files',
        description: `Maximum ${maxFiles} files allowed. You can upload ${maxFiles - attachments.length} more files.`,
        variant: 'destructive',
      });
      return;
    }

    const newAttachments: AttachmentFile[] = [];

    fileArray.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        toast({
          title: 'Invalid File',
          description: `${file.name}: ${validationError}`,
          variant: 'destructive',
        });
        return;
      }

      const attachment: AttachmentFile = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        category: '',
        uploadProgress: 0,
        status: 'pending',
        awbNumber,
        isExisting: false
      };

      newAttachments.push(attachment);
    });

    if (newAttachments.length > 0) {
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [attachments.length, maxFiles, disabled]);

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
    toast({
      title: 'File Removed',
      description: 'The file has been removed from the list.',
    });
  };

  const updateAttachmentCategory = (id: string, category: string) => {
    setAttachments(prev => prev.map(att => 
      att.id === id ? { ...att, category } : att
    ));
  };

  const startUpload = (id: string) => {
    const attachment = attachments.find(att => att.id === id);
    if (attachment && attachment.status === 'pending') {
      performRealUpload(attachment);
    }
  };

  const retryUpload = (id: string) => {
    const attachment = attachments.find(att => att.id === id);
    if (attachment && attachment.status === 'error') {
      setAttachments(prev => prev.map(att => 
        att.id === id ? { ...att, status: 'pending', errorMessage: undefined, uploadProgress: 0 } : att
      ));
      performRealUpload(attachment);
    }
  };

  const startAllUploads = () => {
    attachments
      .filter(att => att.status === 'pending' && att.category && !att.isExisting)
      .forEach(att => performRealUpload(att));
  };

  const downloadFile = async (attachment: AttachmentFile) => {
    if (!attachment.documentId) return;

    try {
      const fileName = attachment.document?.originalName || 
                      attachment.file?.name || 
                      `document_${attachment.documentId}`;
      
      await documentService.downloadDocument(attachment.documentId, fileName);

      toast({
        title: 'Download Started',
        description: `${fileName} download started.`,
      });

    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download file. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const deleteDocument = async (attachment: AttachmentFile) => {
    // For existing documents with documentId, delete from server
    if (attachment.documentId && attachment.isExisting) {
      try {
        await documentService.deleteDocument(attachment.documentId);
        
        setAttachments(prev => prev.filter(att => att.id !== attachment.id));
        
        toast({
          title: 'Document Deleted',
          description: 'Document has been deleted successfully.',
        });

      } catch (error) {
        console.error('Delete document error:', error);
        toast({
          title: 'Delete Failed',
          description: 'Failed to delete document. Please try again.',
          variant: 'destructive',
        });
      }
    } else {
      // For local files without documentId, just remove from list
      setAttachments(prev => prev.filter(att => att.id !== attachment.id));
      
      toast({
        title: 'File Removed',
        description: 'The file has been removed from the list.',
      });
    }
  };

  const requiredCategoryStatus = useMemo(() => {
    const uploadedCategories = new Set(
      attachments
        .filter(att => att.status === 'completed')
        .map(att => att.category)
    );

    return propRequiredCategories.map(category => ({
      category,
      uploaded: uploadedCategories.has(category),
    }));
  }, [attachments, propRequiredCategories]);

  const attachmentStats = useMemo(
    () =>
      attachments.reduce(
        (acc, attachment) => {
          acc.total += 1;
          if (attachment.isExisting) acc.existing += 1;
          if (attachment.status === 'pending') acc.pending += 1;
          if (attachment.status === 'uploading') acc.uploading += 1;
          if (attachment.status === 'completed') acc.completed += 1;
          if (attachment.status === 'error') acc.error += 1;
          if (
            attachment.status === 'pending' &&
            !attachment.category &&
            !attachment.isExisting
          ) {
            acc.pendingWithoutCategory += 1;
          }
          if (
            attachment.status === 'pending' &&
            attachment.category &&
            !attachment.isExisting
          ) {
            acc.pendingWithCategory += 1;
          }
          return acc;
        },
        {
          total: 0,
          pending: 0,
          uploading: 0,
          completed: 0,
          error: 0,
          existing: 0,
          pendingWithoutCategory: 0,
          pendingWithCategory: 0,
        }
      ),
    [attachments]
  );

  const allRequiredUploaded = requiredCategoryStatus.every(status => status.uploaded);
  const hasUploadsInProgress = attachmentStats.uploading > 0;
  const hasPendingUploads = attachmentStats.pendingWithCategory > 0;
  const hasFailedUploads = attachmentStats.error > 0;

  const filteredAttachments = useMemo(() => {
    const query = attachmentQuery.trim().toLowerCase();

    return attachments.filter(attachment => {
      if (attachmentStatusFilter !== 'all') {
        if (attachmentStatusFilter === 'existing') {
          if (!attachment.isExisting) return false;
        } else if (attachment.status !== attachmentStatusFilter) {
          return false;
        }
      }

      if (!query) return true;

      const name =
        attachment.document?.originalName ||
        attachment.document?.fileName ||
        attachment.file?.name ||
        '';
      const category = attachment.category || '';
      const mimeType = attachment.document?.mimeType || attachment.file?.type || '';

      return [name, category, mimeType]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(query));
    });
  }, [attachments, attachmentQuery, attachmentStatusFilter]);

  const hasAttachmentFilters =
    attachmentQuery.trim().length > 0 || attachmentStatusFilter !== 'all';

  const clearAttachmentFilters = () => {
    setAttachmentQuery('');
    setAttachmentStatusFilter('all');
  };

  return (
    <div className="space-y-6">
      {/* Required Categories Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Required Documents
            {!awbNumber && (
              <Badge variant="outline" className="ml-2">
                Draft Mode
              </Badge>
            )}
            {isLoadingExisting && (
              <Badge variant="secondary" className="ml-2">
                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                Loading...
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {requiredCategoryStatus.map(({ category, uploaded }) => (
              <div key={category} className="flex items-center gap-2">
                {uploaded ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-orange-500" />
                )}
                <span className={`text-sm ${uploaded ? 'text-green-700' : 'text-orange-700'}`}>
                  {category}
                </span>
              </div>
            ))}
          </div>
          {!allRequiredUploaded && (
            <p className="text-sm text-muted-foreground mt-2">
              Please upload all required documents to proceed.
            </p>
          )}
          {loadExistingDocuments && awbNumber && awbNumber !== 'temp-awb' && (
            <div className="mt-3 pt-3 border-t flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadExistingDocumentsFromAPI}
                disabled={isLoadingExisting}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingExisting ? 'animate-spin' : ''}`} />
                Refresh Documents
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Upload New Attachments</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {attachmentStats.pendingWithoutCategory > 0 && (
                <Badge variant="outline" className="text-xs">
                  {attachmentStats.pendingWithoutCategory} need category
                </Badge>
              )}
              {hasPendingUploads && (
                <Button
                  onClick={startAllUploads}
                  size="sm"
                  disabled={disabled || hasUploadsInProgress}
                >
                  {hasUploadsInProgress ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload All
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              disabled 
                ? 'border-muted-foreground/10 bg-muted/20 cursor-not-allowed' 
                : dragActive 
                  ? 'border-primary bg-primary/5 cursor-pointer' 
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50 cursor-pointer'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !disabled && document.getElementById('attachment-file-upload')?.click()}
          >
            <Upload className={`h-12 w-12 mx-auto mb-4 ${disabled ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
            <h3 className="text-lg font-medium mb-2">
              {disabled ? 'Upload Disabled' : dragActive ? 'Drop files here' : 'Upload Documents'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {disabled ? 'Form is in read-only mode' : 'Drag and drop files here, or click to select files'}
            </p>
            <Input
              type="file"
              multiple
              accept={acceptedTypes.join(',')}
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              id="attachment-file-upload"
              disabled={disabled}
            />
            {!disabled && (
              <Button 
                type="button" 
                variant="outline"
                onClick={() => document.getElementById('attachment-file-upload')?.click()}
              >
                Choose Files
              </Button>
            )}
            {disabled && (
              <Button type="button" variant="outline" disabled>
                Choose Files
              </Button>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Max {maxFiles} files, {formatFileSize(maxFileSize)} each. 
              Supported: {acceptedTypes.join(', ')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Files List */}
      {attachments.length > 0 && (
        <Card>
          <CardHeader className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span>
                  Documents (
                  {hasAttachmentFilters
                    ? `${filteredAttachments.length} of ${attachments.length}`
                    : attachments.length}
                  )
                </span>
                {hasAttachmentFilters && (
                  <span className="text-xs font-normal text-muted-foreground">
                    Filtered view
                  </span>
                )}
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                {hasFailedUploads && (
                  <Badge variant="destructive" className="text-xs">
                    {attachmentStats.error} Failed
                  </Badge>
                )}
                {attachmentStats.pending > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {attachmentStats.pending} Pending
                  </Badge>
                )}
                {hasUploadsInProgress && (
                  <Badge variant="secondary" className="text-xs">
                    {attachmentStats.uploading} Uploading
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {attachmentStats.completed} Completed
                </Badge>
                {attachmentStats.existing > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {attachmentStats.existing} Existing
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  value={attachmentQuery}
                  onChange={(e) => setAttachmentQuery(e.target.value)}
                  placeholder="Search documents by name, type, or category"
                  className="h-9 w-full sm:max-w-sm"
                />
                <div className="w-full sm:w-44">
                  <Select
                    value={attachmentStatusFilter}
                    onValueChange={(value) =>
                      setAttachmentStatusFilter(
                        value as typeof attachmentStatusFilter
                      )
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="uploading">Uploading</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="error">Failed</SelectItem>
                      <SelectItem value="existing">Existing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hasAttachmentFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAttachmentFilters}
                  className="h-8 px-2"
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {filteredAttachments.length > 0 ? (
              <div className="space-y-4">
                {filteredAttachments.map((attachment) => (
                  <div key={attachment.id} className="flex flex-col gap-2 rounded-lg border p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                      <div className="flex flex-1 items-start gap-3">
                        <div className="mt-0.5 rounded-md bg-muted/60 p-2 text-muted-foreground">
                          {getFileIcon(attachment)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {attachment.document?.originalName ||
                              attachment.file?.name ||
                              'Unknown file'}
                            {attachment.isExisting && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Existing
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {attachment.document?.size
                              ? formatFileSize(attachment.document.size)
                              : attachment.file?.size
                                ? formatFileSize(attachment.file.size)
                                : 'Unknown size'}
                            {attachment.document?.virusScanStatus && (
                              <span
                                className={`ml-2 ${
                                  attachment.document.virusScanStatus === 'CLEAN'
                                    ? 'text-green-600'
                                    : attachment.document.virusScanStatus === 'PENDING'
                                      ? 'text-yellow-600'
                                      : 'text-red-600'
                                }`}
                              >
                                • {attachment.document.virusScanStatus}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between lg:w-auto lg:justify-end">
                        <Select
                          value={attachment.category}
                          onValueChange={(value) =>
                            updateAttachmentCategory(attachment.id, value)
                          }
                          disabled={
                            disabled ||
                            attachment.status === 'uploading' ||
                            attachment.isExisting
                          }
                        >
                          <SelectTrigger className="w-full sm:w-48">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {attachmentCategories.map((category) => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <div className="flex flex-wrap items-center gap-1">
                          {attachment.status === 'pending' &&
                            attachment.category &&
                            !attachment.isExisting && (
                              <Button
                                onClick={() => startUpload(attachment.id)}
                                size="sm"
                                variant="outline"
                                disabled={disabled}
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}

                          {attachment.status === 'uploading' && (
                            <div className="flex items-center gap-2 w-full sm:w-28">
                              <Progress
                                value={attachment.uploadProgress}
                                className="flex-1"
                              />
                              <span className="text-xs w-10 text-right">
                                {attachment.uploadProgress}%
                              </span>
                            </div>
                          )}

                          {attachment.status === 'completed' && (
                            <>
                              <Button
                                onClick={() => downloadFile(attachment)}
                                size="sm"
                                variant="outline"
                                title="Download file"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            </>
                          )}

                          {attachment.status === 'error' && (
                            <>
                              <Button
                                onClick={() => retryUpload(attachment.id)}
                                size="sm"
                                variant="outline"
                                title="Retry upload"
                                disabled={disabled}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <AlertCircle className="h-4 w-4 text-destructive" />
                            </>
                          )}

                          {attachment.isExisting ? (
                            <Button
                              onClick={() => deleteDocument(attachment)}
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              disabled={disabled}
                              title="Delete document"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => removeAttachment(attachment.id)}
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              disabled={disabled || attachment.status === 'uploading'}
                              title="Remove from list"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {attachment.status === 'error' && attachment.errorMessage && (
                      <p className="text-xs text-destructive">{attachment.errorMessage}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No documents match these filters</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Try adjusting the search or status filter to see results.
                </p>
                {hasAttachmentFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAttachmentFilters}
                    className="mt-3"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AttachmentUploader;
